import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Flex, Box, Button, Copy, Image, Check, DotsLoader, WebviewWrapper, CheckButton, ErrorsWrapper, Checkbox } from '@elparking/components'
import { FormattedMessage, injectIntl } from 'react-intl'
import SectionContainer from './SectionContainer'
import FirstNameField from 'commons/js/ui/FirstNameField'
import PhoneField from 'commons/js/ui/PhoneField'
import DateField from 'commons/js/ui/DateField'
import LastNameField from 'commons/js/ui/LastNameField'
import { LocalForm, actions } from 'commons/js/components/form'
import { Control } from 'commons/js/ui/Input'
import messages from './messages'
import styled from 'styled-components'
import { cleanNameString, name, underAgeDate } from 'commons/js/util/validators'
import { Control as CheckControl } from 'commons/js/ui/Checkbox'
import { MUTUAMAS_SERVICES_CONDITIONS } from 'commons/js/constants'
import EmailField from 'commons/js/ui/EmailField'
import TermsAndConditionsModal from '../Modals/TermsAndConditionsModal'
import BottomBar from '../BottomBar'
import { RentalCarEvents, trackTealiumEvent } from 'commons/js/services/analytics'
import useOnCloseModalAnalyticEvent from 'commons/js/hooks/useOnCloseModalAnalyticEvent'

const DRIVER_ME = 'me'
const DRIVER_OTHER = 'other'

const Container = styled.div`
    height: ${({ show }) => show ? 'auto' : '0'};
    min-height: ${({ show }) => show ? '50px' : '0'};
    overflow: hidden;
    transition: min-height 0.5s ease;
`

const WebHeaderComponent = ({user}) => {
    return <><SectionContainer background='success_light' width='100%' padding='0.5em'>
        <Flex alignItems='center'>
            <Image src='/images/icon-ok-success.svg' width='30px' alt='Has iniciado sesión' />
            <Copy color='gray' fontWeight='regular' padding='0 0 0 10px' fontSize='small'>
                <FormattedMessage defaultMessage='Has iniciado sesión con {email}'
                  values={{
                        email: <Copy as='span' padding='0' fontSize='inherit' fontWeight='bold' wordBreak='break-word'>{user.email}</Copy>,
                    }} />
            </Copy>
        </Flex>
    </SectionContainer>
        <Copy fontSize='xxLarge' fontColor='accent_dark' padding='1em 0 1em 0'>
            <FormattedMessage defaultMessage='Hola {name}, revisa y completa la información...' values={{
                    name: user.firstName,
                }} />
        </Copy></>
}
WebHeaderComponent.propTypes = {
    user: PropTypes.object,
}

const CheckUserDataAlert = () => <SectionContainer background='warning20' width='100%' padding='0.5em' marginBottom='1em'>
    <Flex alignItems='center'>
        <Image src='/images/icon-alert.svg' width='30px' alt='Has iniciado sesión' />
        <Copy color='gray' fontWeight='regular' padding='0 0 0 10px' fontSize='small'>
            <FormattedMessage defaultMessage='Revisa que la información esté correcta antes de confirmar y reservar'/>
        </Copy>
    </Flex>
</SectionContainer>
const DriverForm = ({
    user,
    driver: driverInfo,
    intl,
    loading,
    setOnCloseModal,
    onSubmit }) => {
    const [ driver, setDriver ] = useState((driverInfo.sameDriver === undefined || driverInfo.sameDriver) ? DRIVER_ME : DRIVER_OTHER)
    const [showTermsAndConditionsModal, setShowTermsAndConditionsModal] = useState(false)
    const [formSubmitted, setFormSubmitted] = useState(false)
    const [areTermsAccepted, setAreTermsAccepted] = useState(false)
    const isMe = driver === DRIVER_ME
    const checkTermsAndConditions = ({acceptConditions}) => {
        if (!acceptConditions) {
            setShowTermsAndConditionsModal(true)
        }
        return acceptConditions
    }
    useOnCloseModalAnalyticEvent(showTermsAndConditionsModal, RentalCarEvents.showUserDataView)
    useEffect(() => {
        trackTealiumEvent(RentalCarEvents.showUserDataView)
    }, [])

    const CustomCheckBoxControlComponent = ({...restProps}) => <Checkbox checkInputBorderColor={formSubmitted && !areTermsAccepted ? 'error' : undefined} {...restProps} />
    const firstName = cleanNameString(driverInfo.firstName || user.firstName)
    const lastName = cleanNameString(driverInfo.lastName || user.lastName)
    return <Box>
        <LocalForm
          model='driver'
          onSubmit={(values) => {
            setFormSubmitted(true)
            if (checkTermsAndConditions(values)) {
                onSubmit({ sameDriver: isMe, ...values })
            }
          }}
          initialState={{
                firstName: firstName,
                lastName: lastName,
                phone: driverInfo.phone || user.phone,
                birthday: driverInfo.birthday || user.birthday,
                otherFirstName: driverInfo.otherFirstName,
                otherLastName: driverInfo.otherLastName,
                otherPhone: driverInfo.otherPhone,
                otherBirthday: driverInfo.otherBirthday,
                otherEmail: driverInfo.otherEmail,
                fly: driverInfo.fly,
                train: driverInfo.train,
                acceptConditions: false,
            }} >
            <Box p={['0 1em 2em 1em', '0 1em 2em 1em', '0']}>
                {areTermsAccepted && <CheckUserDataAlert />}
                <WebviewWrapper fallbackComponent={<WebHeaderComponent user={user} />}><Box/></WebviewWrapper>
                <Copy fontSize='large' fontWeight='medium' fontColor='black' padding='0 0 1em 0'>
                    <FormattedMessage defaultMessage='¿Eres el conductor principal?'/>
                </Copy>
                <Copy fontSize='large' fontWeight='regular' fontColor='black' padding='0 0 1em 0'>
                    <FormattedMessage defaultMessage='Necesitamos confirmar los datos personales del conductor principal'/>
                </Copy>
                <Box mb='1em' key={`block_${driver}_me`}>
                    <SectionContainer width='100%' padding='1em' withShadow={driver === DRIVER_ME}>
                        <Check
                          justifyContent='start'
                          id={'who_drive_' + DRIVER_ME}
                          model='.who_drive'
                          name='who_drive'
                          type='radio'
                          checked={isMe}
                          dataTest={'who_drive_' + DRIVER_ME}
                          onChange={() => {
                            setDriver(DRIVER_ME)
                            actions.errors = { }
                        }}
                          label={<Copy.span fontSize='medium' fontWeight='book' fontColor='back' padding='0'>
                              <FormattedMessage defaultMessage='Soy el conductor principal' />
                          </Copy.span>} />
                        <Container show={isMe}>
                            <Flex gridGap='10px' mt='1em' pb={['0', '0.3em']} alignItems='start' flexDirection={['column', 'row']}>
                                <Box w={['100%', '100%', '20%']}>
                                    <FirstNameField
                                      disabled
                                      width='100%'
                                      label={intl.formatMessage(messages.firstName)}
                                      placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                      dataTest='driver-first-name-field'
                                      required={isMe}
                                      showClearButton={false}
                                      validators={{
                                            name: name(),
                                        }}
                                      errorMessages={{
                                            name: intl.formatMessage(messages.invalidCharacters),
                                        }} />
                                </Box>
                                <Box w={['100%', '100%', '25%']}>
                                    <LastNameField
                                      disabled
                                      width='100%'
                                      label={intl.formatMessage(messages.lastName)}
                                      placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                      dataTest='driver-first-name-field'
                                      required={isMe}
                                      showClearButton={false}
                                      validators={{
                                            name: name(),
                                        }}
                                      errorMessages={{
                                            name: intl.formatMessage(messages.invalidCharacters),
                                        }}/>
                                </Box>

                                <Box w={['100%', '100%', '35%']}>
                                    <DateField
                                      width='100%'
                                      model='.birthday'
                                      label={intl.formatMessage(messages.date)}
                                      validators={{
                                        underAge: underAgeDate(),
                                    }}
                                      dataTest='driver-birthday-field'
                                      required={isMe} />
                                </Box>
                                <Box w={['100%', '100%', '20%']} paddingBottom={['10px', '0']}>
                                    <PhoneField
                                      width='100%'
                                      validateIcon={false}
                                      dataTest='driver-phone-field'
                                      placeholder={intl.formatMessage(messages.phonePlaceholder)}
                                      required={isMe} />
                                </Box>
                            </Flex>
                        </Container>
                    </SectionContainer>
                </Box>
                <Box mb='1em' key={`block_${driver}_other`}>
                    <SectionContainer width='100%' padding='1em' withShadow={driver === DRIVER_OTHER}>
                        <Check
                          justifyContent='start'
                          name='who_drive'
                          model='who_drive'
                          type='radio'
                          checked={!isMe}
                          dataTest={'who_drive_' + DRIVER_OTHER}
                          id={'who_drive_' + DRIVER_OTHER}
                          onChange={() => setDriver(DRIVER_OTHER)}
                          label={<Copy.span fontSize='medium' fontWeight='book' fontColor='back' padding='0'>
                              <FormattedMessage defaultMessage='El conductor principal es otro' />
                          </Copy.span>} />
                        <Container show={!isMe}>
                            <Flex gridGap='10px' mt='1em' alignItems='start' flexDirection={['column', 'row']}>
                                <Box w={['100%', '100%', '20%']}>
                                    <FirstNameField
                                      model='.otherFirstName'
                                      width='100%'
                                      label={intl.formatMessage(messages.firstName)}
                                      placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                      dataTest='other-driver-first-name-field'
                                      required={!isMe}
                                      validators={{
                                            name: name(),
                                        }}
                                      errorMessages={{
                                            name: intl.formatMessage(messages.invalidCharacters),
                                        }}/>
                                </Box>
                                <Box w={['100%', '100%', '25%']}>
                                    <LastNameField
                                      model='.otherLastName'
                                      width='100%'
                                      label={intl.formatMessage(messages.lastName)}
                                      placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                      dataTest='other-driver-first-name-field'
                                      required={!isMe}
                                      validators={{
                                            name: name(),
                                        }}
                                      errorMessages={{
                                            name: intl.formatMessage(messages.invalidCharacters),
                                        }}/>
                                </Box>
                                <Box w={['100%', '100%', '35%']}>
                                    <DateField
                                      width='100%'
                                      model='.otherBirthday'
                                      label={intl.formatMessage(messages.date)}
                                      validators={{
                                        underAge: underAgeDate(),
                                    }}
                                      dataTest='other-driver-birthday-field'
                                      required={!isMe} />
                                </Box>
                                <Box w={['100%', '100%', '20%']}>
                                    <PhoneField
                                      width='100%'
                                      validateIcon={false}
                                      model='.otherPhone'
                                      dataTest='other-driver-phone-field'
                                      placeholder={intl.formatMessage(messages.phonePlaceholder)}
                                      required={!isMe} />
                                </Box>
                            </Flex>
                            <Flex flexDirection='column'>
                                <Copy fontSize='xSmall' fontWeight='regular' padding='1.5em 0em'><FormattedMessage defaultMessage='Si quieres, podemos enviar también la confirmación de la reserva al conductor principal.'/></Copy>
                                <Box w={['100%', '100%', '50%']}>
                                    <EmailField
                                      model='.otherEmail'
                                      width='100%'
                                      label={intl.formatMessage(messages.email)}
                                      placeholder={intl.formatMessage(messages.emailPlaceholder)}
                                      dataTest='other-driver-email-field'
                                      required={false} />
                                </Box>
                            </Flex>
                        </Container>
                    </SectionContainer>
                </Box>
                <Box mt='2em'>
                    <Copy fontSize='large' fontWeight='medium' fontColor='black' padding='0 0 1em 0'>
                        <FormattedMessage defaultMessage='¿Viajas en avión o tren?'/>
                    </Copy>
                    <Copy fontSize='large' fontWeight='regular' fontColor='black' padding='0 0 1em 0'>
                        <FormattedMessage defaultMessage='Te pedimos esta información para saber si tu vuelo o tren llega con retraso y poder adaptarnos.'/>
                    </Copy>
                    <Flex mt='1em' gridGap='20px' width={['100%', '90%']} flexDirection={['column', 'row']}>
                        <Box w='100%'>
                            <Control
                              model='.fly'
                              dataTest='driver-fly-field'
                              label={intl.formatMessage(messages.fly)}
                              placeholder={intl.formatMessage(messages.flyPlaceholder)}
                              autoComplete='off'
                              debounce={250}
                            />
                        </Box>
                        <Box w='100%'>
                            <Control
                              model='.train'
                              dataTest='driver-train-field'
                              label={intl.formatMessage(messages.train)}
                              placeholder={intl.formatMessage(messages.trainPlaceholder)}
                              autoComplete='off'
                              debounce={250}
                            />
                        </Box>
                    </Flex>
                </Box>
                <Box mt='2em' mb='5em'>
                    <Flex justifyContent='space-between' alignItems='center' width='100%' flexDirection={['column', 'row']}>
                        <Box
                          width={['100%', '60%']}
                          formSubmitted={formSubmitted}
                          areTermsAccepted={areTermsAccepted}
                          marginRight='1em'
                          padding='0.5em'>
                            <CheckControl
                              checkButtonElement={CheckButton.squared}
                              squared
                              model='.acceptConditions'
                              dataTest='accept-conditions-check'
                              fontSize='xSmall'
                              id='acceptConditions'
                              justifyContent='start'
                              label={
                                  <Copy fontSize='xSmall' fontWeight='regular' fontColor='black' padding='1em 1em 1em 0'>
                                      <FormattedMessage defaultMessage='Acepto los {termAndCondition} del servicio' values={{
                                    termAndCondition: <Copy key='terms' textDecoration='underline' fontWeight='medium' fontColor='inherit' fontSize='inherit' font as='a' rel='noopener noreferrer' href={MUTUAMAS_SERVICES_CONDITIONS} target='_blank'>
                                        <FormattedMessage
                                          defaultMessage='Términos y Condiciones'
                                    />
                                    </Copy>}} />
                                  </Copy>
                            }
                              onChange={(value) => {
                                    setAreTermsAccepted(value.target.checked)
                                    setFormSubmitted(false)
                              }}
                              customCheckBoxComponent={CustomCheckBoxControlComponent}
                            />
                            {formSubmitted && !areTermsAccepted && (
                                <ErrorsWrapper>
                                    <Copy fontSize='xxSmall' color='error' padding='0 0.5em' >
                                        {intl.formatMessage(messages.checked)}
                                    </Copy>
                                </ErrorsWrapper>
                            )}
                        </Box>
                        <Box width={['100%', '40%']} display={['none', 'block', 'block']}>
                            <Button width='100%' height='auto' type='submit' disabled={loading} dataTest='save-driver' >
                                {loading
                                ? <DotsLoader />
                                : <FormattedMessage defaultMessage='Confirmar y reservar' />
                            }
                            </Button>
                        </Box>
                    </Flex>
                </Box>
            </Box>
            <Box display={['block', 'none', 'none']}>
                <BottomBar
                  setOnCloseModal={setOnCloseModal}
                  display={['block', 'none']}
                  disabled={loading}
                  onNext={null}
                  continueText={<FormattedMessage defaultMessage='Confirmar y reservar' />}
                />
            </Box>
        </LocalForm>
        <TermsAndConditionsModal isShown={showTermsAndConditionsModal} onClose={() => setShowTermsAndConditionsModal(false)} onAccept={() => setShowTermsAndConditionsModal(false)} />
    </Box>
}

DriverForm.defaultProps = {
    loading: false,
    onSubmit: () => {},
    setOnCloseModal: () => {},
}

DriverForm.propTypes = {
    setOnCloseModal: PropTypes.func,
    user: PropTypes.shape({
        email: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        birthday: PropTypes.any,
        phone: PropTypes.string,
    }),
    loading: PropTypes.bool,
    driver: PropTypes.object,
    onSubmit: PropTypes.func,
    intl: PropTypes.object,
}

export default injectIntl(DriverForm)
